// ── Studio / School System ────────────────────────────────────────────────────
export const StudioPermissions = {
  users: {
    create: "users:create",
    read: "users:read",
    update: "users:update",
    delete: "users:delete",
    ban: "users:ban",
  },
  master: {
    create: "master:create",
    read: "master:read",
    update: "master:update",
    delete: "master:delete",
    reorder: "master:reorder",
  },
  students: {
    create: "students:create",
    read: "students:read",
    update: "students:update",
    delete: "students:delete",
  },
  packages: {
    create: "packages:create",
    read: "packages:read",
    update: "packages:update",
    delete: "packages:delete",
    sell: "packages:sell",
  },
  privateLessons: {
    create: "private-lessons:create",
    read: "private-lessons:read",
    update: "private-lessons:update",
    delete: "private-lessons:delete",
    sell: "private-lessons:sell",
  },
  classes: {
    create: "classes:create",
    read: "classes:read",
    update: "classes:update",
    delete: "classes:delete",
  },
  classEnrollments: {
    read: "class-enrollments:read",
    update: "class-enrollments:update",
    convert: "class-enrollments:convert",
  },
  roomBookings: {
    create: "room-bookings:create",
    read: "room-bookings:read",
    update: "room-bookings:update",
    cancel: "room-bookings:cancel",
  },
  roomDisplays: {
    read: "room-displays:read",
    update: "room-displays:update",
  },
  billing: {
    create: "billing:create",
    read: "billing:read",
    void: "billing:void",
  },
  refunds: {
    create: "refunds:create",
    read: "refunds:read",
    approve: "refunds:approve",
    execute: "refunds:execute",
  },
  reports: {
    read: "reports:read",
  },
  payroll: {
    read: "payroll:read",
    confirm: "payroll:confirm",
  },
  packageNotifications: {
    read: "package-notifications:read",
    send: "package-notifications:send",
    manage: "package-notifications:manage",
  },
} as const;

// ── Expenses / Organization spending ─────────────────────────────────────────
export const ExpensesPermissions = {
  expenses: {
    create: "expenses:create",
    read: "expenses:read",
    update: "expenses:update",
    approve: "expenses:approve",
    pay: "expenses:pay",
    void: "expenses:void",
    manageCategories: "expenses:manage-categories",
  },
} as const;

// ── Events & Registration ─────────────────────────────────────────────────────
export const EventsPermissions = {
  campRegistrations: {
    read: "camp-registrations:read",
    update: "camp-registrations:update",
    convert: "camp-registrations:convert",
    export: "camp-registrations:export",
  },
  campEvents: {
    read: "camp-events:read",
    update: "camp-events:update",
  },
  jamRegistrations: {
    read: "jam-registrations:read",
    update: "jam-registrations:update",
    convert: "jam-registrations:convert",
    export: "jam-registrations:export",
  },
  jamEvents: {
    read: "jam-events:read",
    update: "jam-events:update",
  },
  workshopRegistrations: {
    read: "workshop-registrations:read",
    update: "workshop-registrations:update",
    export: "workshop-registrations:export",
  },
  workshopEvents: {
    read: "workshop-events:read",
    update: "workshop-events:update",
  },
  visitorLottery: {
    read: "visitor-lottery:read",
    write: "visitor-lottery:write",
    export: "visitor-lottery:export",
  },
  audienceVote: {
    read: "audience-vote:read",
    write: "audience-vote:write",
    export: "audience-vote:export",
  },
} as const;

// ── Performance / Show Management ─────────────────────────────────────────────
export const PerformancePermissions = {
  performanceRequests: {
    create: "performance-requests:create",
    read: "performance-requests:read",
    update: "performance-requests:update",
    delete: "performance-requests:delete",
  },
  performanceEvents: {
    create: "performance-events:create",
    read: "performance-events:read",
    update: "performance-events:update",
    delete: "performance-events:delete",
  },
  performers: {
    create: "performers:create",
    read: "performers:read",
    update: "performers:update",
    delete: "performers:delete",
  },
  performanceRoles: {
    create: "performance-roles:create",
    read: "performance-roles:read",
    update: "performance-roles:update",
    delete: "performance-roles:delete",
  },
  performancePerDiem: {
    create: "performance-per-diem:create",
    read: "performance-per-diem:read",
    update: "performance-per-diem:update",
    delete: "performance-per-diem:delete",
  },
  performanceRoleFees: {
    read: "performance-role-fees:read",
    update: "performance-role-fees:update",
  },
  performanceAssignments: {
    create: "performance-assignments:create",
    read: "performance-assignments:read",
    update: "performance-assignments:update",
    delete: "performance-assignments:delete",
  },
  performancePriority: {
    read: "performance-priority:read",
    update: "performance-priority:update",
  },
  performancePricing: {
    create: "performance-pricing:create",
    read: "performance-pricing:read",
    update: "performance-pricing:update",
    delete: "performance-pricing:delete",
  },
  performanceDocuments: {
    create: "performance-documents:create",
    read: "performance-documents:read",
    issue: "performance-documents:issue",
    void: "performance-documents:void",
    delete: "performance-documents:delete",
  },
  performancePaymentTerms: {
    create: "performance-payment-terms:create",
    read: "performance-payment-terms:read",
    update: "performance-payment-terms:update",
    delete: "performance-payment-terms:delete",
  },
  performanceBankAccount: {
    create: "performance-bank-account:create",
    read: "performance-bank-account:read",
    update: "performance-bank-account:update",
    delete: "performance-bank-account:delete",
  },
  performancePayouts: {
    create: "performance-payouts:create",
    read: "performance-payouts:read",
    approve: "performance-payouts:approve",
    markPaid: "performance-payouts:mark-paid",
    delete: "performance-payouts:delete",
  },
  performanceReports: {
    read: "performance-reports:read",
  },
} as const;

// ── Forms / Public form builder ──────────────────────────────────────────────
export const FormsPermissions = {
  forms: {
    create: "forms:create",
    read: "forms:read",
    update: "forms:update",
    delete: "forms:delete",
  },
} as const;

// ── Combined (backward-compatible) ────────────────────────────────────────────
export const Permissions = {
  ...StudioPermissions,
  ...ExpensesPermissions,
  ...EventsPermissions,
  ...PerformancePermissions,
  ...FormsPermissions,
} as const;

// ── Permission groups (used for rendering sections in RoleForm) ────────────────
export const PERMISSION_GROUPS = [
  {
    system: "studio" as const,
    label: "ລະບົບສຕູດິໂອ / ໂຮງຮຽນ",
    permissions: StudioPermissions,
  },
  {
    system: "expenses" as const,
    label: "ບັນທຶກຄ່າໃຊ້ຈ່າຍ",
    permissions: ExpensesPermissions,
  },
  {
    system: "events" as const,
    label: "Events & Registration",
    permissions: EventsPermissions,
  },
  {
    system: "performance" as const,
    label: "ລະບົບຈັດການງານສະແດງ",
    permissions: PerformancePermissions,
  },
  {
    system: "forms" as const,
    label: "ຟອມ (Form Builder)",
    permissions: FormsPermissions,
  },
] as const;

export const ALL_PERMISSIONS = Object.entries(Permissions).flatMap(
  ([resource, actions]) =>
    Object.entries(actions).map(([action, id]) => ({ id, resource, action })),
);

export type PermissionId = (typeof ALL_PERMISSIONS)[number]["id"];

export const VALID_PERMISSION_ID_SET = new Set<string>(
  ALL_PERMISSIONS.map((p) => p.id),
);

/** ກັ່ນສິດທີ່ບໍ່ມີໃນ code ອີກແລ້ວ (ເຊັ່ນ audit:read ຈາກ seed ເກົ່າ) */
export function sanitizePermissionIds(ids: string[]): PermissionId[] {
  return ids.filter((id): id is PermissionId =>
    VALID_PERMISSION_ID_SET.has(id),
  );
}

// ── Human-friendly labels ──────────────────────────────────────────────────────
const STUDIO_RESOURCE_LABELS: Record<string, string> = {
  users: "ຜູ້ໃຊ້",
  master: "ຂໍ້ມູນປະເພດພື້ນຖານ",
  students: "ນັກຮຽນ",
  packages: "ແພັກເກດ",
  "private-lessons": "ແພັກເກດຮຽນສ່ວນຕົວ",
  classes: "ຄລາດສ໌",
  classEnrollments: "ລົງທະບຽນຮຽນຄລາດສ໌",
  "class-enrollments": "ລົງທະບຽນຮຽນຄລາດສ໌",
  "room-bookings": "ການຈອງຫ້ອງ",
  roomDisplays: "ຈໍສະແດງຜົນ",
  "room-displays": "ຈໍສະແດງຜົນ",
  billing: "ການຊຳລະ",
  refunds: "ຄຳຂໍຄືນເງິນ",
  reports: "ລາຍງານ",
  payroll: "ຄ່າຈ້າງຄູ (payroll)",
  packageNotifications: "ແຈ້ງເຕືອນແພັກ WhatsApp",
  "package-notifications": "ແຈ້ງເຕືອນແພັກ WhatsApp",
};

const EXPENSES_RESOURCE_LABELS: Record<string, string> = {
  expenses: "ບັນທຶກຄ່າໃຊ້ຈ່າຍ",
};

const EVENTS_RESOURCE_LABELS: Record<string, string> = {
  campRegistrations: "ລົງທະບຽນ Camp",
  "camp-registrations": "ລົງທະບຽນ Camp",
  campEvents: "ກິດກຳ Camp",
  "camp-events": "ກິດກຳ Camp",
  jamRegistrations: "ລົງທະບຽນ Jam",
  "jam-registrations": "ລົງທະບຽນ Jam",
  jamEvents: "ກິດກຳ Jam",
  "jam-events": "ກິດກຳ Jam",
  workshopRegistrations: "ລົງທະບຽນ Workshop",
  "workshop-registrations": "ລົງທະບຽນ Workshop",
  workshopEvents: "ກິດກຳ Workshop",
  "workshop-events": "ກິດກຳ Workshop",
  visitorLottery: "ລົງທະບຽນຜູ້ເຂົ້າຊົມ + ລຸ້ນລາງວັນ",
  "visitor-lottery": "ລົງທະບຽນຜູ້ເຂົ້າຊົມ + ລຸ້ນລາງວັນ",
  audienceVote: "โหวตผู้ชม (Audience Vote)",
  "audience-vote": "โหวตผู้ชม (Audience Vote)",
};

const PERFORMANCE_RESOURCE_LABELS: Record<string, string> = {
  // camelCase — RoleForm object keys
  performanceRequests: "ຄິວຄຳຂໍ",
  performanceEvents: "ງານສະແດງ",
  performers: "ນັກສະແດງ",
  performanceRoles: "ບົດບາດນາຍສະແດງ",
  performancePerDiem: "ເບ້ຍລ້ຽງຕ່າງແຂວງ",
  "performance-per-diem": "ເບ້ຍລ້ຽງຕ່າງແຂວງ",
  performanceRoleFees: "ຄ່າຕົວຕາມບົດບາດ",
  "performance-role-fees": "ຄ່າຕົວຕາມບົດບາດ",
  "performance-roles": "ບົດບາດນักแสด",
  performanceAssignments: "ການຈັດນັກສະແດງ",
  performancePriority: "ລຳດັບຄວາມສຳຄັນ",
  performancePricing: "ລາຍການລາຄາ",
  performanceDocuments: "ເອກະສານ (ໃບສະເໜີລາຄາ / ໃບເກັບເງິນ / ໃບຮັບເງິນ)",
  performancePaymentTerms: "ແມ່ແບບເງື່ອນໄຂການຊຳລະ",
  performanceBankAccount: "ແມ່ແບບບັນຊີທະນາຄານ",
  performancePayouts: "ໃບເບີກຈ່າຍນັກສະແດງ",
  performanceReports: "ລາຍງານງານສະແດງ",
  // kebab-case — permission id resource segment
  "performance-requests": "ຄິວຄຳຂໍ",
  "performance-events": "ງານສະແດງ",
  "performance-assignments": "ການຈັດນັກສະແດງ",
  "performance-priority": "ລຳດັບຄວາມສຳຄັນ",
  "performance-pricing": "ລາຍການລາຄາ",
  "performance-documents": "ເອກະສານ (ໃບສະເໜີລາຄາ / ໃບເກັບເງິນ / ໃບຮັບເງິນ)",
  "performance-payment-terms": "ແມ່ແບບເງື່ອນໄຂການຊຳລະ",
  "performance-bank-account": "ແມ່ແບບບັນຊີທະນາຄານ",
  "performance-payouts": "ໃບເບີກຈ່າຍນັກສະແດງ",
  "performance-reports": "ລາຍງານງານສະແດງ",
};

const FORMS_RESOURCE_LABELS: Record<string, string> = {
  forms: "ຟອມ (Form Builder)",
};

export const RESOURCE_LABELS: Record<string, string> = {
  ...STUDIO_RESOURCE_LABELS,
  ...EXPENSES_RESOURCE_LABELS,
  ...EVENTS_RESOURCE_LABELS,
  ...PERFORMANCE_RESOURCE_LABELS,
  ...FORMS_RESOURCE_LABELS,
};

export const ACTION_LABELS: Record<string, string> = {
  create: "ສ້າງ",
  read: "ເບິ່ງ",
  update: "ແກ້ໄຂ",
  delete: "ລຶບ",
  ban: "ລະງັບ",
  sell: "ຂາຍ",
  cancel: "ຍົກເລີກ",
  approve: "ອະນຸມັດ",
  execute: "ດຳເນີນການ",
  issue: "ອອກເອກະສານ",
  void: "ຍົກເລີກເອກະສານ",
  reorder: "ຈັດລຳດັບ",
  markPaid: "ໝາຍວ່າຈ່າຍແລ້ວ",
  confirm: "ຢືນຢັນຮອບ",
  write: "ແກ້ໄຂ",
  export: "ສົ່ງອອກ",
  convert: "ປ່ຽນເປັນນັກຮຽນ",
  all: "ທັງໝົດ",
  "manage-categories": "ຈັດການຫມວດ",
};

export function getResourceLabel(resource: string): string {
  return RESOURCE_LABELS[resource] ?? resource;
}

export function getActionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

export function getPermissionLabel(id: PermissionId): string {
  const [resource, action] = (id as string).split(":");
  return `${getActionLabel(action ?? "")} ${getResourceLabel(resource ?? "")}`;
}

export function getPermissionLabels(ids: PermissionId[]): string[] {
  return ids.map((id) => getPermissionLabel(id));
}
